import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { PreventiveMaintenanceRecord } from '../../../interfaces/customer-api';
import { PMCommentsModal } from '../PMCommentsModal/PMCommentsModal';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useEffects, useOvermindState } from '../../state';
import { Spinner } from '../Spinner/Spinner';
import { useAsyncData, ApiCallStatus } from '../../hooks';
import { getKalmarCareLink, getKalmarMaintenanceLink } from '../../utils';
import css from './PreventiveMaintenanceTable.module.scss';

export const PreventiveMaintenanceTable: React.FC = () => {
    const { t } = useTranslation('MyEquipmentItem');
    const [selectedReport, setSelectedReport] = useState<PreventiveMaintenanceRecord | null>(null);
    const [loadingSoNumber, setLoadingSoNumber] = useState<string | null>(null);
    const { api } = useEffects();
    const { selectedCustomerNumber, selectedEquipmentId, selectedEquipment } = useOvermindState();

    const loadPMRecords = () =>
        api.loadPMRecords(selectedCustomerNumber, selectedEquipment?.equipment_number || '');
    const [reports, isLoading, apiCallStatus] = useAsyncData<PreventiveMaintenanceRecord[]>(
        loadPMRecords,
        [],
        [selectedEquipment?.equipment_number, selectedCustomerNumber],
    );

    const [hasFetched, setHasFetched] = useState(false);
    React.useEffect(() => {
        if (!isLoading) setHasFetched(true);
    }, [isLoading]);

    const handleOpenPdf = async (soNumber: string) => {
        if (!selectedCustomerNumber || !selectedEquipmentId) return;
        setLoadingSoNumber(soNumber);
        try {
            const url = await api.loadPMReportLink(selectedCustomerNumber, selectedEquipmentId, soNumber);
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            }
        } finally {
            setLoadingSoNumber(null);
        }
    };

    return (
        <>
            <h3 data-cy="pm-heading">{t('Preventive maintenance reports')}</h3>
            {!hasFetched || isLoading ? (
                <Spinner size={3} />
            ) : apiCallStatus === ApiCallStatus.INTERNAL_SERVER_ERROR ? (
                <p className={css.noData} data-cy="pm-error">{t('Preventive maintenance reports are temporarily unavailable')}</p>
            ) : reports.length === 0 ? (
                <div className={css.noData} data-cy="pm-empty">
                    <p>
                        {t(
                            'Preventive maintenance reports are generated after certified Kalmar service inspections. Book a service inspection with our local team to keep your equipment running reliably.',
                        )}
                    </p>
                    <a href={getKalmarMaintenanceLink()} target="_blank" rel="noopener noreferrer">
                        {t('Learn more about Kalmar maintenance')}
                    </a>
                    <a href={getKalmarCareLink()} target="_blank" rel="noopener noreferrer">
                        {t('Learn more about Kalmar Care')}
                    </a>
                </div>
            ) : (
                <div className={css.tableWrapper}>
                    <table className={css.table} data-cy="pm-table">
                        <thead>
                            <tr>
                                <th>{t('Service order')}</th>
                                <th>{t('Maintenance interval')}</th>
                                <th>{t('Date')}</th>
                                <th>{t('Faults')}</th>
                                <th>{t('Comments')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {reports.map((report) => (
                                <tr key={report.serviceOrderNumber}>
                                    <td>{report.serviceOrderNumber}</td>
                                    <td>{report.maintenanceInterval}</td>
                                    <td>{DateTime.fromISO(report.date).toFormat('dd.MM.yyyy')}</td>
                                    <td>{report.faults}</td>
                                    <td>
                                        <button
                                            className={css.commentsButton}
                                            data-cy="pm-comments-btn"
                                            onClick={() => setSelectedReport(report)}
                                            title={t('View comments')}
                                        >
                                            <span>{report.commentsCount}</span>
                                            <ChatBubbleOutlineIcon sx={{ fontSize: 24 }}/>
                                        </button>
                                    </td>
                                    <td>
                                       {report.reportDownloadUrl === null ? (
                                           <span className={css.noPdf}>{t('No PDF')}</span>
                                       ) : (
                                           <button
                                               className={css.pdfLink}
                                               data-cy="pm-pdf-btn"
                                               onClick={() => handleOpenPdf(report.serviceOrderNumber)}
                                               disabled={loadingSoNumber === report.serviceOrderNumber}
                                           >
                                               {loadingSoNumber === report.serviceOrderNumber ? (
                                                   <Spinner size={1.4} />
                                               ) : (
                                                   <>
                                                       {t('Open pdf')}
                                                       <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
                                                   </>
                                               )}
                                           </button>
                                       )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedReport && (
                <PMCommentsModal
                    isOpen={true}
                    onClose={() => setSelectedReport(null)}
                    comments={selectedReport.comments}
                />
            )}
        </>
    );
};
